/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { MessageInline, MessageVariety, Text, TextSize } from '@sonarsource/echoes-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useStandardExperienceModeQuery } from '~sq-server-commons/queries/mode';
import { SandboxInstanceSettings } from '../../types';
import { mapQualitiesAndSeveritiesToLabels } from '../../utils';

interface InstanceDefaultPermittedQualitiesMessageProps {
  instanceSettings?: SandboxInstanceSettings;
}

export function InstanceDefaultPermittedQualitiesMessage({
  instanceSettings,
}: Readonly<InstanceDefaultPermittedQualitiesMessageProps>) {
  const { data: isStandardExperience } = useStandardExperienceModeQuery();
  const intl = useIntl();

  const defaultQualities = mapQualitiesAndSeveritiesToLabels(
    intl,
    instanceSettings,
    isStandardExperience,
  );

  if (!defaultQualities) {
    return null;
  }

  return (
    <MessageInline as="div" className="sw-mt-3" size={TextSize.Small} variety={MessageVariety.Info}>
      <FormattedMessage
        id={`settings.sandbox_issues.project.default_settings.${isStandardExperience ? 'standard' : 'mqr'}`}
      />
      <ul>
        {defaultQualities.map((q) => (
          <li className="sw-mt-1 sw-list-disc sw-ml-1" key={q.label}>
            <Text colorOverride="echoes-color-text-info" isHighlighted size={TextSize.Small}>
              {q.label}
            </Text>
            <span> - </span>
            <FormattedMessage
              id="settings.sandbox_issues.project.default_settings.severities"
              values={{
                severities: q.severities.join(', '),
                count: q.severities.length,
              }}
            />
          </li>
        ))}
      </ul>
    </MessageInline>
  );
}
